/**
 * Delbara jämförelselänkar: /elavtal/jamfor?omrade=SE3&forbrukning=20000
 * (valfritt postnummer= och avtalstyp=).
 *
 * Länken öppnar samma resultat som sökflödet (/sokresultat) utan att
 * formuläret behöver skickas. Parametrarna översätts här till sökflödets
 * egna nycklar (elomrade, consumption, postalCode, contractType), så att
 * resultatkomponenten och dess logik återanvänds oförändrade.
 */

import { getElomradeFromPostalCode } from "@/lib/elomrade";

export const COMPARE_PATH = "/elavtal/jamfor";

/** Parametrarna som gör /elavtal/jamfor till en resultatsida. */
export const COMPARE_PARAM_KEYS = ["omrade", "forbrukning", "postnummer", "avtalstyp"] as const;

export const COMPARE_CONTRACT_TYPES = ["manadspris", "fast", "kvartspris", "timpris"] as const;
export type CompareContractType = (typeof COMPARE_CONTRACT_TYPES)[number];

/** Förbrukning när länken saknar forbrukning= (villa utan elvärme). */
export const DEFAULT_CONSUMPTION = 5000;
export const MIN_CONSUMPTION = 500;
export const MAX_CONSUMPTION = 200_000;

export interface CompareParams {
  area: "SE1" | "SE2" | "SE3" | "SE4";
  consumption: number;
  postalCode: string | null;
  contractType: CompareContractType | null;
}

type RawSearch = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === "string" || typeof v === "number" ? String(v).trim() : "");

const CONTRACT_ALIASES: Record<string, CompareContractType> = {
  manadspris: "manadspris",
  "månadspris": "manadspris",
  rorligt: "manadspris",
  "rörligt": "manadspris",
  fast: "fast",
  fastpris: "fast",
  kvartspris: "kvartspris",
  kvart: "kvartspris",
  timpris: "timpris",
  tim: "timpris",
};

/** Plockar ut och validerar jämförelseparametrarna ur en (rå) querystring. */
export function pickCompareSearch(search: RawSearch): Partial<Record<(typeof COMPARE_PARAM_KEYS)[number], string>> {
  const out: Partial<Record<(typeof COMPARE_PARAM_KEYS)[number], string>> = {};
  for (const key of COMPARE_PARAM_KEYS) {
    const value = str(search[key]);
    if (value) out[key] = value;
  }
  return out;
}

/** Har URL:en någon jämförelseparameter? Då är sidan en parametersida (noindex). */
export function hasCompareParams(search: RawSearch): boolean {
  return Object.keys(pickCompareSearch(search)).length > 0;
}

/**
 * Tolkar länkens parametrar. Elområdet tas från omrade= eller härleds ur
 * postnummer=. Returnerar null om inget elområde går att få fram – då visas
 * den vanliga jämförelsesidan med sökformuläret.
 */
export function parseCompareParams(search: RawSearch): CompareParams | null {
  const raw = pickCompareSearch(search);

  const digits = (raw.postnummer ?? "").replace(/\D+/g, "");
  const postalCode = digits.length === 5 ? digits : null;

  const omrade = (raw.omrade ?? "").toUpperCase().replace(/\s+/g, "");
  const areaFromParam = /^SE[1-4]$/.test(omrade) ? omrade : /^[1-4]$/.test(omrade) ? `SE${omrade}` : null;
  const area = (areaFromParam ?? (postalCode ? getElomradeFromPostalCode(postalCode) : null)) as
    | CompareParams["area"]
    | null;
  if (!area) return null;

  const kwh = Number((raw.forbrukning ?? "").replace(/[\s_.]/g, ""));
  const consumption =
    Number.isFinite(kwh) && kwh >= MIN_CONSUMPTION && kwh <= MAX_CONSUMPTION
      ? Math.round(kwh)
      : DEFAULT_CONSUMPTION;

  const contractType = CONTRACT_ALIASES[(raw.avtalstyp ?? "").toLowerCase()] ?? null;

  return { area, consumption, postalCode, contractType };
}

/** Sökflödets dolda sök-state (samma nycklar som /sokresultat?...). */
export function toSearchResultQuery(p: CompareParams): string {
  const q = new URLSearchParams();
  if (p.postalCode) q.set("postalCode", p.postalCode);
  q.set("elomrade", p.area);
  q.set("consumption", String(p.consumption));
  if (p.contractType) q.set("contractType", p.contractType);
  return q.toString();
}

/** Kanonisk delbar länk för en jämförelse. */
export function buildCompareUrl(p: Partial<CompareParams>, base = ""): string {
  const q = new URLSearchParams();
  if (p.area) q.set("omrade", p.area);
  if (p.consumption) q.set("forbrukning", String(p.consumption));
  if (p.postalCode) q.set("postnummer", p.postalCode);
  if (p.contractType) q.set("avtalstyp", p.contractType);
  return `${base}${COMPARE_PATH}?${q.toString()}`;
}

const CONTRACT_LABELS: Record<CompareContractType, string> = {
  manadspris: "månadspris",
  fast: "fast pris",
  kvartspris: "kvartspris",
  timpris: "timpris",
};

export const compareContractLabel = (t: CompareContractType | null) => (t ? CONTRACT_LABELS[t] : null);

/** Rubrik för resultatsidan, t.ex. "Elavtal i SE3 för 20 000 kWh per år". */
export function compareHeading(p: CompareParams): string {
  const kwh = new Intl.NumberFormat("sv-SE").format(p.consumption);
  const type = compareContractLabel(p.contractType);
  return `${type ? `Elavtal med ${type}` : "Elavtal"} i ${p.area} för ${kwh} kWh per år`;
}

/** Flik i sökresultatet som länken öppnar (samma regel som resolveContractTab). */
export function compareTab(t: CompareContractType | null): "manadspris" | "fast" | "kvartspris" {
  if (t === "fast") return "fast";
  if (t === "kvartspris" || t === "timpris") return "kvartspris";
  return "manadspris";
}

/** Hör ett avtal (dess avtalstyp i fritext) till fliken? */
export function contractInTab(contractType: string, tab: ReturnType<typeof compareTab>): boolean {
  const v = contractType.toLowerCase();
  if (tab === "fast") return /fast|bund/.test(v);
  if (tab === "kvartspris") return /kvart|tim|spot/.test(v);
  return /månad|manad|rörl|rorl|variabel|variable/.test(v);
}

export interface CompareSummaryRow {
  company: string;
  contractType: string;
  /** Jämförpris i öre/kWh enligt elbolagets uppgift, eller null. */
  comparativePrice: number | null;
  /** Förbrukningsnivån jämförpriset avser (2 000/5 000/20 000 kWh). */
  comparativeTier: number | null;
  binding: string | null;
}

export interface CompareSummary {
  rows: CompareSummaryRow[];
  /** Antal avtal i fliken och antal elbolag bakom dem. */
  total: number;
  companies: number;
}

/** Rader för den serverrenderade sammanfattningen: lägst jämförpris först, ett avtal per bolag. */
export function summarizeResults(
  results: {
    companyName: string;
    contractType?: string;
    priceInfo: { comparativePrice?: number };
    comparativeTier?: number;
    bindingPeriod?: { text?: string };
  }[],
  p: CompareParams,
  limit = 15,
): CompareSummary {
  const tab = compareTab(p.contractType);
  const inTab = results.filter((r) => contractInTab(r.contractType ?? "Månadspris", tab));
  const sorted = [...inTab].sort(
    (a, b) => (a.priceInfo.comparativePrice ?? Infinity) - (b.priceInfo.comparativePrice ?? Infinity),
  );
  const seen = new Set<string>();
  const rows: CompareSummaryRow[] = [];
  for (const r of sorted) {
    const key = r.companyName.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      company: r.companyName,
      contractType: r.contractType ?? "Månadspris",
      comparativePrice: r.priceInfo.comparativePrice ?? null,
      comparativeTier: r.priceInfo.comparativePrice != null ? (r.comparativeTier ?? null) : null,
      binding: r.bindingPeriod?.text ?? null,
    });
    if (rows.length >= limit) break;
  }
  return {
    rows,
    total: inTab.length,
    companies: new Set(inTab.map((r) => r.companyName.trim().toLowerCase())).size,
  };
}

/**
 * Dokumentation av länkformatet för AI-agenter (/llms.txt och /for-llms).
 * Exemplen byggs med buildCompareUrl så att de alltid följer parsern.
 */
export const COMPARE_LINK_EXAMPLES: { title: string; url: string; note: string }[] = [
  {
    title: "Elavtal i SE3 för 20 000 kWh/år",
    url: buildCompareUrl({ area: "SE3", consumption: 20000 }, "https://elmarknad.se"),
    note: "omrade=SE1–SE4, forbrukning=årsförbrukning i kWh (500–200 000, standard 5 000)",
  },
  {
    title: "Fastprisavtal för postnummer 41301",
    url: buildCompareUrl({ postalCode: "41301", contractType: "fast" }, "https://elmarknad.se"),
    note: "postnummer= ger elområdet om omrade= saknas; avtalstyp=manadspris, fast, kvartspris eller timpris",
  },
];

export const COMPARE_LINK_INTRO =
  "Länka direkt till ett jämförelseresultat med GET-parametrar på /elavtal/jamfor – sidan visar samma resultat som sökflödet utan formulär. Resultatsidorna är noindex med canonical till /elavtal.";
